/**
 * GEO 개선 에이전트 — `access: 'agent'` 전용 베어러 토큰 (261023-geo-watch-agent).
 * 운영자 PC 에서 하루 한 번 도는 래퍼(`tools/geo-agent/run.mjs`)가 부른다.
 *
 * ★ **`API_ROUTES` 에 넣지 않는다** — `publish.ts`·`worker.ts` 와 같은 자리다(access-matrix.test.ts).
 * ★ **읽기는 개선에 필요한 것만**: 감시 목록 · 브랜드가 빠진 우리 사이트 인용 샘플 · 브랜드 프로필.
 *   토큰은 레포에 두지 않는다(`deploy/secrets/` 금지 — 루트 `.env` 와 Secret Manager). 고객 데이터를 읽기 때문이다.
 * ★ **쓰기는 케이스 표 하나**뿐이다(올리기·닫기). 프로필·질문·브랜드를 고치는 길은 없다 — 프로필은 사람이 인터뷰 뒤 고친다.
 */
import { z } from 'zod';
import { IdParams, IsoDateTime, KstDate, Ok } from './common.ts';
import { defineRoute } from './route.ts';

const Win = z.object({
  n: z.number().int(),
  k: z.number().int(),
  lo: z.number().nullable(),
  hi: z.number().nullable(),
});
const RateWindows = z.object({ recent: Win, base: Win, long: Win });

export const WatchQuery = z.object({
  queryId: z.uuid(),
  query: z.string(),
  brandId: z.uuid(),
  brandName: z.string(),
  /** 판정에 쓰는 브랜드 표기 — 페이지에 이 표기가 있는지 본다 */
  brandNames: z.array(z.string()),
  states: z.object({
    mention: z.enum(['ok', 'drop', 'rise', 'low']),
    site: z.enum(['ok', 'drop', 'rise', 'low']),
    match: z.enum(['ok', 'low']),
  }),
  mention: RateWindows,
  site: RateWindows,
  match: Win,
});
export type WatchQuery = z.infer<typeof WatchQuery>;

export const watchlist = defineRoute({
  method: 'get',
  path: '/internal/agent/watchlist',
  access: 'agent',
  summary: '살아 있는 고객 질문의 GEO 노출 지표와 판정 — 창은 가장 늦은 스냅샷에서 끝난다',
  response: z.object({ snapshotDate: KstDate.nullable(), queries: z.array(WatchQuery) }),
});

export const AgentCitation = z.object({
  position: z.number().int(),
  url: z.string(),
  title: z.string().nullable(),
  /** 우리 사이트면 그 사이트의 url_prefix, 아니면 null */
  ourSite: z.string().nullable(),
});

export const UncitedSample = z.object({
  id: z.uuid(),
  createdAt: IsoDateTime,
  answerText: z.string(),
  citations: z.array(AgentCitation),
  /** 장소 카드 업체명 */
  placeCard: z.array(z.string()),
});

export const uncitedMentions = defineRoute({
  method: 'get',
  path: '/internal/agent/queries/{id}/uncited-mentions',
  access: 'agent',
  summary: '우리 사이트를 인용했는데 브랜드가 언급되지 않은 최근 적격 샘플(최신순)',
  params: IdParams,
  query: z.object({ limit: z.coerce.number().int().min(1).max(50).default(30) }),
  response: z.object({ samples: z.array(UncitedSample) }),
});

export const agentBrand = defineRoute({
  method: 'get',
  path: '/internal/agent/brands/{id}',
  access: 'agent',
  summary: '브랜드 프로필(고객이 준 사실의 원천) — 에이전트는 이 값만 사실로 쓴다',
  params: IdParams,
  response: z.object({
    id: z.uuid(),
    name: z.string(),
    brandNames: z.array(z.string()),
    profile: z.string(),
    profileUpdatedAt: IsoDateTime.nullable(),
  }),
});

/* ── 인터뷰 준비 케이스 ─────────────────────────────────────────────── */

/** 순위 산정표 한 줄 — 항목별 점수는 그 사이트 산식의 이름 그대로(예: specialists·doctors·tenure) */
export const CaseRankRow = z.object({
  rank: z.number().int(),
  name: z.string(),
  /** 우리 고객인가 — 화면이 강조한다 */
  ours: z.boolean(),
  total: z.number(),
  parts: z.record(z.string(), z.number()),
});
export const CaseRankingTable = z.object({
  site: z.string(),
  pageTitle: z.string(),
  /** 항목 이름 → 화면 표기·만점 */
  columns: z.array(z.object({ key: z.string(), label: z.string(), max: z.number() })),
  rows: z.array(CaseRankRow),
  candidates: z.number().int(),
  /** 산식 이름(예: hanbang-v1) · 계산한 스냅샷 기준일 */
  formula: z.string(),
  asOf: KstDate,
});
export type CaseRankingTable = z.infer<typeof CaseRankingTable>;

/** 인터뷰 질문 — 점수 구간 경계에서 결정적으로 만든다 */
export const CaseQuestion = z.object({
  /** 산식 항목 키 */
  part: z.string(),
  question: z.string(),
  /** 지금 반영된 값과 그 출처(심평원·네이버·고객 확인) */
  current: z.string(),
  /** 다음 구간까지의 조건과 얻는 점수 — 예 「3명 이상이면 +1」 */
  gain: z.string(),
});
export type CaseQuestion = z.infer<typeof CaseQuestion>;

export const upsertCase = defineRoute({
  method: 'post',
  path: '/internal/agent/cases',
  access: 'agent',
  summary: '인터뷰 준비 케이스 올리기 — 같은 (질문, 페이지)의 열린 케이스가 있으면 내용을 갱신한다',
  body: z
    .object({
      queryId: z.uuid(),
      pageUrl: z.string().url().max(1000),
      analysisMd: z.string().min(1).max(20_000),
      sampleIds: z.array(z.uuid()).min(1).max(10),
      rankingTable: CaseRankingTable.nullable(),
      questions: z.array(CaseQuestion).max(30),
    })
    .strict(),
  response: z.object({ id: z.uuid(), created: z.boolean() }),
});

export const OpenCase = z.object({
  id: z.uuid(),
  brandId: z.uuid(),
  queryId: z.uuid(),
  pageUrl: z.string(),
  createdAt: IsoDateTime,
  /** 브랜드 프로필이 케이스 뒤에 바뀌었는가 — 래퍼가 이것부터 처리한다 */
  profileUpdatedAfter: z.boolean(),
});

export const openCases = defineRoute({
  method: 'get',
  path: '/internal/agent/cases',
  access: 'agent',
  summary: '열린 케이스 — 프로필이 갱신된 것이 먼저 온다',
  response: z.object({ cases: z.array(OpenCase) }),
});

export const resolveCase = defineRoute({
  method: 'post',
  path: '/internal/agent/cases/{id}/resolve',
  access: 'agent',
  summary: '케이스 닫기 — 프로필 반영 뒤 결과(순위 변화 전후·커밋)를 남긴다',
  params: IdParams,
  body: z.object({ note: z.string().min(1).max(4000) }).strict(),
  response: Ok,
});
